/**
 * TeamChatButton
 *
 * Self-contained trigger button. Lives in the sidebar footer (util-row).
 * - relative container; the trigger button holds a ref so the dropdown can
 *   anchor a portaled, position:fixed panel that opens UP and to the RIGHT
 *   (the sidebar has a `transform` stacking context + an `overflow-hidden`
 *   ancestor, so an inline `absolute` panel would be clipped/hidden).
 * - outside-click handled by a document `mousedown` listener inside the panel
 *   (same pattern as NotificationsDropdown), which works across the portal.
 * - no native `title` attribute (aria-label only).
 *
 * Single badge on the button icon (top-right):
 * - Red:   unread message count (priority)
 * - Green: online users count (shown only when no unread)
 */

import React, { useState, useRef } from 'react';
import { useTeamUsers } from '../hooks/useTeamUsers';
import { useTeamUnreadCount } from '../hooks/useTeamUnreadCount';
import { useTeamChatManager } from './TeamChatManager';
import { TeamChatDropdown } from './TeamChatDropdown';
import type { TeamUserWithPresence } from '@troia-v3/types';
import { Icon } from '@/shared/icons'

interface TeamChatButtonProps {
  /** 'sm' = sidebar (32px/18px), 'md' = header mobile (38px/22px) */
  size?: 'sm' | 'md';
}

export const TeamChatButton: React.FC<TeamChatButtonProps> = ({ size = 'sm' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const btnSize = size === 'md' ? 38 : 32;
  const iconSize = size === 'md' ? 22 : 18;

  const { data: users = [], isLoading } = useTeamUsers();
  const { totalUnread, unreadByUser, clearUnreadForUser } = useTeamUnreadCount();
  const { openChat } = useTeamChatManager();

  const onlineCount = users.filter((u) => u.isOnline).length;

  // Outside-click is handled by a document `mousedown` listener inside TeamChatDropdown
  // (works across the portal — checks both the trigger and panel refs).

  const handleUserClick = (user: TeamUserWithPresence) => {
    clearUnreadForUser(user.id);
    openChat(user);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        className="flex items-center justify-center rounded-lg focus:outline-none transition-colors relative"
        style={{ width: btnSize, height: btnSize, color: 'var(--theme-card-text)' }}
        onClick={() => setIsOpen((v) => !v)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        aria-label="Chat em equipe"
        data-testid="team-chat-button"
      >
        <Icon name="users" style={{ width: iconSize, height: iconSize }} />

        {/* `.util .badge` do mockup: 16px, 9px bold, top/right -2. unread (red) > online (green) */}
        {totalUnread > 0 ? (
          <span
            className="absolute flex items-center justify-center rounded-full font-bold"
            style={{ top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px', fontSize: 9, lineHeight: 1, backgroundColor: '#EF4444', color: '#ffffff' }}
          >
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        ) : onlineCount > 0 ? (
          <span
            className="absolute flex items-center justify-center rounded-full font-bold"
            style={{ top: -2, right: -2, minWidth: 16, height: 16, padding: '0 4px', fontSize: 9, lineHeight: 1, backgroundColor: '#22c55e', color: '#ffffff' }}
          >
            {onlineCount > 99 ? '99+' : onlineCount}
          </span>
        ) : null}
      </button>

      {isOpen && (
        <TeamChatDropdown
          triggerRef={triggerRef}
          users={users}
          isLoading={isLoading}
          unreadByUser={unreadByUser}
          onClose={() => setIsOpen(false)}
          onUserClick={handleUserClick}
        />
      )}
    </div>
  );
};
